import React, { useEffect, useRef } from 'react';
import { HttpKeyValue } from '../../../types/rest/dslTypes';
import BlazeTable from '../../blazeTable/BlazeTable';
import { BlazeTableOptions } from '../../blazeTable/BlazeTable.types';
import _ from 'lodash';
import { Button } from 'react-bootstrap';
import BzmIcon from '../../icons/BzmIcon';
import StatefulInput from '../../forms/StatefulInput/StatefulInput';
import { useIsMount } from '../../../hooks/useIsMountHook';
import { generateUniqueId } from '../../../utils/stringUtils';

interface KeyValueTableProps {
  keyValues: HttpKeyValue[]; // Array of key-value pairs to display in the table
  onChange: (keyValues: HttpKeyValue[]) => void; // Callback triggered when the key-value pairs are updated
  onTransfer?: (keyValues: HttpKeyValue[]) => void; // Optional callback for transferring key-value pairs
  title: string; // Title of the table
}

interface HttpKeyValueItem extends HttpKeyValue {
  id: number;
}

const KeyValueTable = ({ keyValues, title, onChange, onTransfer }: KeyValueTableProps) => {
  // Initialize table items with keyValues, assigning unique IDs
  const mapTableItems = keyVals =>
    _.map(keyVals, item => ({ ...item, id: generateUniqueId(_.map(keyVals, item => item.id)) }));

  const [tableItems, setTableItems] = React.useState<HttpKeyValueItem[]>(mapTableItems(keyValues));

  useEffect(() => {
    if (
      !isMount &&
      (_.size(keyValues) !== _.size(tableItems) ||
        !_.isEqual(
          _.map(keyValues, ({ name, value }) => ({ name, value })),
          _.map(tableItems, ({ name, value }) => ({ name, value })),
        ))
    ) {
      setTableItems(mapTableItems(keyValues));
    }
  }, [keyValues]);

  const [selected, setSelected] = React.useState<HttpKeyValueItem[]>([]);
  const isMount = useIsMount();
  const tableRef = useRef<BlazeTable>(null);

  useEffect(() => {
    if (!isMount) {
      onChange(tableItems);
    }
  }, [tableItems]);

  const updateHandler = (itemId: number, field: string | number, value: any) => {
    setTableItems(prevItems => prevItems.map(item => (item.id === itemId ? { ...item, [field]: value } : item)));
  };

  const getTableOptions = (): BlazeTableOptions => ({
    pageable: false,
    selectable: true,
    showRowExpander: false,
    customTableClassName: 'flex-table-simple flex-table-condensed',
    columnDef: [
      {
        header: 'Name',
        accessor: 'name',
        cellClass: 'flex-table-col-overflow-visible',
        cellComponent: (item: HttpKeyValueItem) => {
          return <StatefulInput value={item.name} onChange={value => updateHandler(item.id, 'name', value)} />;
        },
      },
      {
        header: 'Value',
        accessor: 'value',
        cellClass: 'flex-table-col-overflow-visible',
        cellComponent: (item: HttpKeyValueItem) => {
          return <StatefulInput value={item.value} onChange={value => updateHandler(item.id, 'value', value)} />;
        },
      },
      {
        header: 'Actions',
        accessor: 'actions',
        width: 80,
        cellComponent: item => {
          return (
            <div className={'flex-table-actions text-center'}>
              <Button
                variant={'link'}
                className={'btn-icon-only btn-no-hover btn-link-dark'}
                onClick={() => {
                  const updatedItems = tableItems.filter(tableItem => tableItem.id !== item.id);
                  setTableItems(updatedItems);
                  setSelected(selected.filter(selectedItem => selectedItem.id !== item.id));
                }}
              >
                <BzmIcon variant={'icon-bzm-delete'} />
              </Button>

              <span title={item.name === '' || item.value === '' ? 'Cannot transfer empty key-value' : undefined}>
                <Button
                  disabled={item.name === '' || item.value === ''}
                  variant={'link'}
                  className={'btn-icon-only btn-no-hover btn-link-dark'}
                  onClick={() => {
                    if (onTransfer) {
                      onTransfer([item]);
                    }
                  }}
                >
                  <BzmIcon variant={'icon-bzm-request'} />
                </Button>
              </span>
            </div>
          );
        },
      },
    ],
  });

  const selectedSnippet = _.size(selected) > 0 ? `(SELECTED: ${_.size(selected)})` : '';

  const onDeleteHandler = (items: HttpKeyValueItem[]) => {
    const updatedItems = tableItems.filter(item => !items.some(selectedItem => selectedItem.id === item.id));
    setTableItems(updatedItems);
    setSelected([]);
  };

  return (
    <div>
      <div className={'d-flex justify-content-between align-items-center mb-3'}>
        <div>
          <h5 className={'mb-0 font-weight-semi-bold d-inline-block'}>
            List of {title} {selectedSnippet}
          </h5>

          {/* Bulk Actions */}
          <Button disabled={_.size(selected) === 0} variant={'link'} className={'btn-icon-only btn-no-hover ml-4 p-0'}>
            <BzmIcon variant={'icon-bzm-delete'} onClick={() => onDeleteHandler(selected)} />
          </Button>

          <Button disabled={_.size(selected) === 0} variant={'link'} className={'btn-icon-only btn-no-hover ml-1 p-0'}>
            <BzmIcon
              title={'Transfer selected key-values to the Transaction Definition.'}
              variant={'icon-bzm-request'}
              onClick={() => {
                onTransfer(selected);
                setSelected([]);
                tableRef.current?.deSelectAll();
              }}
            />
          </Button>
        </div>

        <Button
          size={'sm'}
          className={'btn-circular'}
          onClick={() => {
            const newItem: HttpKeyValueItem = {
              id: generateUniqueId(_.map(tableItems, item => item.id)),
              name: '',
              value: '',
            };
            setTableItems([...tableItems, newItem]);
          }}
        >
          <BzmIcon variant={'icon-bzm-add'} />
        </Button>
      </div>
      <BlazeTable
        onSelectionChange={(selectedRows: HttpKeyValueItem[]) => {
          setSelected(selectedRows);
        }}
        selectedItems={selected.map(item => item.id)}
        isLoading={false}
        options={getTableOptions()}
        data={tableItems}
        showPageSizeDropdown={false}
        ref={tableRef}
      />
    </div>
  );
};

export default KeyValueTable;
